import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { TrendingUp, TrendingDown, ChevronRight, X, Check } from 'lucide-react';
import { DS, GS, hairline, eyebrow } from '../lib/designTokens';
import type { DevReviewProposal } from '../lib/dev-review';
import { tapLight, tapMedium } from '../lib/haptics';

interface Props {
  proposals: DevReviewProposal[];
  onAccept: (proposal: DevReviewProposal) => Promise<void>;
  onSkip: (proposal: DevReviewProposal) => void;
  onClose: () => void;
}

const initialsOf = (name: string) =>
  (name || '?').trim().split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();

const AttributeDots = ({ value, proposed, max = 5 }: { value: number; proposed: number; max?: number }) => (
  <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
    {Array.from({ length: max }, (_, i) => {
      const filled = i < value;
      const wouldFill = i < proposed;
      const isNew = wouldFill && !filled;
      const isRemoved = filled && !wouldFill;
      return (
        <div key={i} style={{
          width: '10px', height: '10px', borderRadius: '50%',
          backgroundColor: isNew ? GS.green : isRemoved ? DS.red : filled ? DS.textPri : DS.bgAlt,
          border: isNew ? 'none' : isRemoved ? 'none' : filled ? 'none' : hairline,
          transition: 'background-color 0.2s',
        }} />
      );
    })}
  </div>
);

const OVRPill = ({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
    <span style={{ fontSize: '9px', fontWeight: 600, color: DS.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
    <span style={{ fontSize: '26px', fontWeight: 700, color: highlight ? GS.greenInk : DS.textPri, lineHeight: 1 }}>{value}</span>
  </div>
);

export const PlayerDevReviewModal = ({ proposals, onAccept, onSkip, onClose }: Props) => {
  const [index, setIndex] = useState(0);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [skipped, setSkipped] = useState<Set<string>>(new Set());

  const remaining = proposals.filter(p => !accepted.has(p.playerId) && !skipped.has(p.playerId));
  const current = remaining[0] ?? null;
  const done = remaining.length === 0;

  // xOVR reveal — a soft tap the moment the divergence (and its xOVR) is shown.
  useEffect(() => { tapLight(); }, []);

  const handleAccept = async () => {
    if (!current || accepting) return;
    tapMedium(); // deliberate confirm — the rating is being committed
    setAccepting(true);
    await onAccept(current);
    setAccepted(prev => new Set(prev).add(current.playerId));
    setAccepting(false);
  };

  const handleSkip = () => {
    if (!current) return;
    onSkip(current);
    setSkipped(prev => new Set(prev).add(current.playerId));
  };

  const isUp = current && current.gap > 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        style={{
          width: '100%', maxWidth: '480px',
          backgroundColor: DS.bg,
          borderRadius: '20px 20px 0 0',
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 20px 16px' }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: 700, color: DS.textPri }}>Development Review</div>
            <div style={{ fontSize: '12px', color: DS.textSec, marginTop: '2px' }}>
              {done ? 'All caught up' : `${remaining.length} player${remaining.length !== 1 ? 's' : ''} to review`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: DS.textMuted }}>
            <X size={20} />
          </button>
        </div>

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              style={{ padding: '24px 20px 32px', textAlign: 'center' }}
            >
              <div style={{
                width: '52px', height: '52px', borderRadius: '50%',
                backgroundColor: DS.bgAlt, margin: '0 auto 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Check size={24} color={DS.textSec} />
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: DS.textPri, marginBottom: '8px' }}>Roster reviewed</div>
              <div style={{ fontSize: '13px', color: DS.textSec, lineHeight: 1.5 }}>
                {accepted.size > 0
                  ? `${accepted.size} player rating${accepted.size !== 1 ? 's' : ''} updated. OVR will reflect the changes immediately.`
                  : 'No changes made. Check back after a few more games.'}
              </div>
              <button
                onClick={onClose}
                style={{
                  marginTop: '24px', padding: '12px 32px',
                  backgroundColor: DS.textPri, color: DS.bg,
                  border: 'none', borderRadius: '10px',
                  fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Done
              </button>
            </motion.div>
          ) : (
            <motion.div
              key={current?.playerId}
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.18 }}
            >
              {/* Player identity */}
              <div style={{ borderTop: hairline, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: DS.textPri, color: DS.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', fontWeight: 700, flexShrink: 0,
                }}>
                  {current?.playerNumber || initialsOf(current?.playerName || '')}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: DS.textPri }}>{current?.playerName}</div>
                  <div style={{ fontSize: '12px', color: DS.textSec, marginTop: '2px' }}>{current?.primaryPosition || 'Player'}</div>
                </div>
                {isUp
                  ? <TrendingUp size={20} color={GS.greenInk} />
                  : <TrendingDown size={20} color={DS.red} />
                }
              </div>

              {/* OVR comparison */}
              <div style={{ borderTop: hairline, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '32px' }}>
                <OVRPill label="Scouted OVR" value={current?.currentOVR ?? 0} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <ChevronRight size={16} color={DS.textMuted} />
                  <span style={{ fontSize: '10px', color: isUp ? GS.greenInk : DS.red, fontWeight: 700 }}>
                    {isUp ? '+' : ''}{current?.gap ?? 0}
                  </span>
                </div>
                <OVRPill label="xOVR" value={current?.xOVR ?? 0} highlight />
              </div>

              {/* Stat line */}
              <div style={{ borderTop: hairline, padding: '10px 20px' }}>
                <span style={{ ...eyebrow, fontSize: '9px' }}>Based on</span>
                <div style={{ fontSize: '13px', color: DS.textSec, marginTop: '3px' }}>{current?.statLine}</div>
              </div>

              {/* Proposed change */}
              <div style={{ borderTop: hairline, padding: '14px 20px' }}>
                <span style={{ ...eyebrow, fontSize: '9px' }}>Proposed change</span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: DS.textPri }}>{current?.attributeLabel}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <AttributeDots value={current?.currentValue ?? 0} proposed={current?.proposedValue ?? 0} />
                    <span style={{ fontSize: '13px', color: DS.textSec }}>
                      {current?.currentValue} → <span style={{ fontWeight: 700, color: isUp ? GS.greenInk : DS.red }}>{current?.proposedValue}</span>
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: DS.textSec, marginTop: '10px', lineHeight: 1.5 }}>
                  {current?.reason}
                </div>
              </div>

              {/* Actions */}
              <div style={{ borderTop: hairline, display: 'flex', padding: '12px 16px', gap: '10px' }}>
                <button
                  onClick={handleSkip}
                  style={{
                    flex: 1, padding: '12px',
                    backgroundColor: DS.bgAlt, color: DS.textSec,
                    border: hairline, borderRadius: '10px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Skip for now
                </button>
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  style={{
                    flex: 2, padding: '12px',
                    backgroundColor: DS.textPri, color: DS.bg,
                    border: 'none', borderRadius: '10px',
                    fontSize: '14px', fontWeight: 600,
                    cursor: accepting ? 'default' : 'pointer',
                    opacity: accepting ? 0.7 : 1,
                  }}
                >
                  {accepting ? 'Saving…' : `Update ${current?.attributeLabel} to ${current?.proposedValue}`}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};
